import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, KanbanSquare, Pencil, Plus, Trash2, Workflow, Zap } from 'lucide-react';
import { PageHeader, EmptyState } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Skeleton, Switch } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ActionEditor } from '@/features/automation/workflows-page';
import {
  ACTION_LABELS,
  useDeleteWorkflow,
  useSaveWorkflow,
  useSetWorkflowStatus,
  useWorkflowCatalogue,
  useWorkflows,
  type Action,
  type WorkflowRule,
} from '@/features/automation/api';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError } from '@/lib/api';
import { usePipelines } from './api';
import type { PipelineEntityType, PipelineStage } from './types';

type Unit = 'minutes' | 'hours' | 'days';
const UNIT_MINUTES: Record<Unit, number> = { minutes: 1, hours: 60, days: 1440 };

function describeDelay(minutes: number): string {
  if (!minutes) return 'Immediately';
  if (minutes % 1440 === 0) return `After ${minutes / 1440} day${minutes === 1440 ? '' : 's'}`;
  if (minutes % 60 === 0) return `After ${minutes / 60} hour${minutes === 60 ? '' : 's'}`;
  return `After ${minutes} min`;
}

function splitDelay(minutes: number): { amount: string; unit: Unit } {
  if (!minutes) return { amount: '', unit: 'hours' };
  if (minutes % 1440 === 0) return { amount: String(minutes / 1440), unit: 'days' };
  if (minutes % 60 === 0) return { amount: String(minutes / 60), unit: 'hours' };
  return { amount: String(minutes), unit: 'minutes' };
}

/**
 * The Digital Pipeline (AmoCRM): the board drawn sideways, and under each
 * column what happens to a record the moment it lands there — or a while
 * later, if it is still there. "Lead reaches Proposal → message them, make a
 * task for the owner; two days on, still here → remind the manager."
 *
 * Underneath, each card is an ordinary automation rule with a stage on it,
 * so it shows in the automation log like every other rule.
 */
export function DigitalPipelinePage() {
  const { entityType: raw } = useParams();
  const entityType: PipelineEntityType = raw === 'deal' ? 'deal' : 'lead';
  const { data: pipelines = [], isLoading } = usePipelines(entityType);
  const [pipelineId, setPipelineId] = useState('');
  const pipeline = pipelines.find((item) => item.id === pipelineId) ?? pipelines.find((item) => item.isDefault) ?? pipelines[0];

  const { data: rules = [] } = useWorkflows({ entityType });
  const { can } = usePermission();
  const [editing, setEditing] = useState<{ stage: PipelineStage; rule?: WorkflowRule } | null>(null);

  const byStage = useMemo(() => {
    const map = new Map<string, WorkflowRule[]>();
    for (const rule of rules) {
      const stageId = rule.triggerType === 'stage_changed' ? (rule.triggerConfig?.stageId as string | undefined) : undefined;
      if (!stageId) continue;
      map.set(stageId, [...(map.get(stageId) ?? []), rule]);
    }
    return map;
  }, [rules]);

  const noun = entityType === 'lead' ? 'lead' : 'deal';

  return (
    <div>
      <PageHeader
        title="Digital pipeline"
        description={`What happens automatically when a ${noun} lands in each stage — right away, or after a wait if it is still there.`}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to={entityType === 'lead' ? '/crm/leads/board' : '/crm/deals'}>
                <KanbanSquare className="h-4 w-4" />
                Board
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/workflows">
                <Workflow className="h-4 w-4" />
                Run log
              </Link>
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border p-0.5">
          {(['lead', 'deal'] as const).map((type) => (
            <Link
              key={type}
              to={`/crm/digital-pipeline/${type}`}
              className={`rounded-md px-3 py-1.5 text-sm ${type === entityType ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {type === 'lead' ? 'Lead pipelines' : 'Deal pipelines'}
            </Link>
          ))}
        </div>
        {pipelines.length > 1 && (
          <Select value={pipeline?.id ?? ''} onChange={(event) => setPipelineId(event.target.value)} className="h-9 w-auto min-w-[12rem]">
            {pipelines.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Select>
        )}
      </div>

      {isLoading ? (
        <div className="flex gap-3 overflow-hidden">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-80 w-72 shrink-0" />
          ))}
        </div>
      ) : !pipeline ? (
        <EmptyState icon={<KanbanSquare className="h-6 w-6" />} title="No pipeline yet" description="Set up a pipeline first, then give its stages automations." />
      ) : (
        <div className="scrollbar-thin flex gap-3 overflow-x-auto pb-4">
          {pipeline.stages.map((stage, index) => {
            const stageRules = byStage.get(stage.id) ?? [];
            return (
              <div key={stage.id} className="flex w-72 shrink-0 flex-col">
                <div
                  className="relative rounded-t-xl px-3 py-2.5 text-sm font-semibold text-white"
                  style={{ backgroundColor: stage.colour }}
                >
                  <span className="me-1 opacity-70">{index + 1}.</span>
                  {stage.name}
                  <span className="float-right text-xs font-normal opacity-80">
                    {stageRules.length} automation{stageRules.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="flex-1 space-y-2 rounded-b-xl border border-t-0 bg-muted/30 p-2">
                  {stageRules.map((rule) => (
                    <AutomationCard
                      key={rule.id}
                      rule={rule}
                      onEdit={() => setEditing({ stage, rule })}
                    />
                  ))}
                  {can('admin.workflow.create') && (
                    <button
                      onClick={() => setEditing({ stage })}
                      className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed py-3 text-xs text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add automation
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && pipeline && (
        <AutomationDialog
          entityType={entityType}
          pipelineId={pipeline.id}
          stage={editing.stage}
          stages={pipeline.stages}
          rule={editing.rule}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function AutomationCard({ rule, onEdit }: { rule: WorkflowRule; onEdit: () => void }) {
  const setStatus = useSetWorkflowStatus();
  const remove = useDeleteWorkflow();
  const delay = Number(rule.triggerConfig?.delayMinutes ?? 0);
  const active = rule.status === 'ACTIVE';

  return (
    <div className={`rounded-lg border bg-background p-2.5 shadow-sm ${active ? '' : 'opacity-60'}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        {delay ? <Clock className="h-3 w-3" /> : <Zap className="h-3 w-3 text-amber-500" />}
        {describeDelay(delay)}
        <Can permission="admin.workflow.update">
          <Switch
            className="ms-auto scale-75"
            checked={active}
            onCheckedChange={(checked) => setStatus.mutate({ id: rule.id, status: checked ? 'ACTIVE' : 'PAUSED' })}
          />
        </Can>
      </div>
      <ul className="mt-1.5 space-y-0.5">
        {(rule.actions ?? []).map((action, index) => (
          <li key={index} className="text-sm">
            {ACTION_LABELS[action.type] ?? action.type}
            {summary(action) && <span className="block truncate text-xs text-muted-foreground">{summary(action)}</span>}
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
        <span>Ran {rule.runCount}×</span>
        {rule.errorCount > 0 && <span className="text-destructive">{rule.errorCount} failing</span>}
        <Can permission="admin.workflow.update">
          <button onClick={onEdit} className="ms-auto hover:text-foreground" title="Edit">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </Can>
        <Can permission="admin.workflow.delete">
          <button onClick={() => remove.mutate(rule.id)} className="hover:text-destructive" title="Delete">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </Can>
      </div>
    </div>
  );
}

function summary(action: Action): string {
  const config = action.config ?? {};
  return String(config.text ?? config.title ?? config.subject ?? config.tag ?? config.url ?? '');
}

function AutomationDialog({
  entityType,
  pipelineId,
  stage,
  stages,
  rule,
  onClose,
}: {
  entityType: PipelineEntityType;
  pipelineId: string;
  stage: PipelineStage;
  stages: PipelineStage[];
  rule?: WorkflowRule;
  onClose: () => void;
}) {
  const { data: catalogue } = useWorkflowCatalogue();
  const save = useSaveWorkflow();
  const initialDelay = splitDelay(Number(rule?.triggerConfig?.delayMinutes ?? 0));
  const [when, setWhen] = useState<'now' | 'later'>(initialDelay.amount ? 'later' : 'now');
  const [amount, setAmount] = useState(initialDelay.amount || '1');
  const [unit, setUnit] = useState<Unit>(initialDelay.unit);
  const [actions, setActions] = useState<Action[]>(
    rule?.actions?.length ? rule.actions : [{ type: 'create_task', config: { title: `Follow up: {{firstName}}`, dueInDays: 1 } }],
  );

  const available = (catalogue?.actions ?? []).filter((type) => type !== 'create_ticket' || entityType === 'deal');
  const delayMinutes = when === 'later' ? Math.max(1, Number(amount) || 0) * UNIT_MINUTES[unit] : 0;

  const submit = () => {
    const name = `${stage.name} · ${describeDelay(delayMinutes)} · ${actions.map((a) => ACTION_LABELS[a.type] ?? a.type).join(', ')}`.slice(0, 150);
    save.mutate(
      {
        id: rule?.id,
        // Kept unique per rule: the stage, the timing and the actions read as its name.
        name: rule ? rule.name : `${name} #${Date.now().toString(36).slice(-4)}`,
        description: `Digital pipeline — ${stage.name}`,
        triggerType: 'stage_changed',
        entityType,
        triggerConfig: { stageId: stage.id, pipelineId, delayMinutes, digitalPipeline: true },
        conditions: null,
        actions,
        status: rule?.status ?? 'ACTIVE',
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            When a {entityType} lands in <span style={{ color: stage.colour }}>{stage.name}</span>
          </DialogTitle>
          <DialogDescription>
            A delayed step is cancelled if the {entityType} has moved on by then.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label>Timing</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={when} onChange={(event) => setWhen(event.target.value as 'now' | 'later')} className="h-9 w-auto">
              <option value="now">Immediately</option>
              <option value="later">After a wait</option>
            </Select>
            {when === 'later' && (
              <>
                <Input
                  type="number"
                  min={1}
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  className="h-9 w-20"
                />
                <Select value={unit} onChange={(event) => setUnit(event.target.value as Unit)} className="h-9 w-auto">
                  <option value="minutes">minutes</option>
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                </Select>
                <span className="text-sm text-muted-foreground">if it is still here</span>
              </>
            )}
          </div>
        </div>

        <ActionEditor
          actions={actions}
          available={available}
          writableFields={catalogue?.writableFields?.[entityType] ?? []}
          entityType={entityType}
          onChange={setActions}
          stages={stages.filter((item) => item.id !== stage.id)}
        />

        {save.error && <FormError message={(save.error as ApiRequestError).message} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || actions.length === 0}>
            {rule ? 'Save' : 'Add automation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
